import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { type SceneCategory, VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveOverlayRangeOperation } from './overlay-placement';
import { computeSceneShortIds } from './scene-addressing';
import { validateSceneDesignId } from './scene-design-validation';
import { getInterleavedTranscript } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import {
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
} from 'js/src/data/templates/templates-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { moveOtherOverlaysAndCreateOverlayOperation } from 'js/src/luminary/luminary-operations';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isOverlaySceneDesign,
    isSceneAnOverlay,
    isVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

export const legacyAddVoiceoverSceneSchema = z.object({
    scene_group_index: z
        .number()
        .describe('The index of the scene group to add the scene to (0-based)'),
    words: z
        .array(z.string())
        .optional()
        .describe(
            'Array of consecutive tokens from the transcript where the new scene should appear. These must appear sequentially in the transcript exactly as spelled, including any `<pause seconds="N"/>` token that falls between them (write each pause exactly as it appears in the transcript). Omit when cover_entire_video is true.',
        ),
    cover_entire_video: z
        .boolean()
        .optional()
        .describe(
            'When true, the new scene spans the whole scene group — from the very first frame (including leading silence before the first word) to the end. Use instead of words ONLY for full-video treatments like an audiogram waveform design, on a scene group that has no other added scenes yet. It is rejected once the group already has added scenes because it would replace them all; to fill an uncovered range in a built-out video, pass words for just the uncovered words instead.',
        ),
    scene_design_id: z
        .string()
        .describe('The ID of the scene design to use for the new scene (e.g., "minimal:text_1")'),
});
export type AddVoiceoverSceneArgs = z.infer<typeof legacyAddVoiceoverSceneSchema>;

export class LegacyAddVoiceoverSceneTool extends VideoStateTool<
    typeof legacyAddVoiceoverSceneSchema
> {
    protected override readonly schema = legacyAddVoiceoverSceneSchema;
    protected override readonly toolName = TOOL_NAMES.addVoiceoverScene;
    protected override readonly toolDescription = `Add a new scene to a voiceover or talking-head scene group, either over a specific sequence of transcript words or spanning the entire video. Word-bounded scenes start at the first given word, so they never cover leading silence — pass cover_entire_video for full-video treatments (e.g. a waveform/audiogram design) that must own the very first frame. Use ${TOOL_NAMES.listSceneDesigns} first to find a suitable design (e.g. a lower third), passing narrative_thread="talking_head" for a talking-head scene group or narrative_thread="voiceover" for a voiceover scene group. Choose ${TOOL_NAMES.addVoiceoverScene} when the audio structure should stay the same, the user wants emphasis on specific words, or you're adding a visual moment during speech. (To create a structural break in the narration instead, use ${TOOL_NAMES.splitSceneGroup}.)`;

    public override getToolDisplayName(): string {
        return 'Adding voiceover scene';
    }

    protected override onEditApplied(args: AddVoiceoverSceneArgs): void {
        // A scene added to the first scene group renders over the first scene, so it
        // changes the thumbnail.
        if (args.scene_group_index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'layers';
    }

    public async execute(args: AddVoiceoverSceneArgs, context: ToolExecutionContext) {
        const {
            scene_group_index,
            words: requestedWords,
            cover_entire_video,
            scene_design_id,
        } = args;
        const { parentActionGroup } = context;

        // Validate scene group index
        if (scene_group_index < 0 || scene_group_index >= this.luminary.sceneGroups.length) {
            return `Error: scene_group_index ${scene_group_index} is out of bounds. Valid range is 0 to ${this.luminary.sceneGroups.length - 1}`;
        }
        const sceneGroup = this.luminary.sceneGroups[scene_group_index];

        // Check if scene group has a transcript. transcriptTokens is the canonical interleaved
        // (word + pause) list the agent reads, so the words it passes back match token-for-token.
        const transcript = sceneGroup.originalTranscript;
        const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);

        if (!transcript || transcriptTokens.length === 0) {
            return `Error: Scene group at index ${scene_group_index} does not have a transcript. Only voiceover and talking-head scene groups with transcripts can have scenes added to them.`;
        }

        if (cover_entire_video && requestedWords && requestedWords.length > 0) {
            return 'Error: pass either words or cover_entire_video, not both.';
        }
        if (!cover_entire_video && (!requestedWords || requestedWords.length === 0)) {
            return 'Error: words array cannot be empty. Provide the words to cover, or set cover_entire_video: true to span the whole video.';
        }

        const validation = validateSceneDesignId(this.luminary, scene_design_id);
        if (validation.error !== undefined) {
            return validation.error;
        }
        const currentTemplateId = this.luminary.template.id;

        // Added scenes inherit the design's category, and only voice-driven compatible
        // categories are recognized as scenes that can be added this way (and accepted
        // as base scenes by future scene creation), so reject incompatible designs
        // upfront — same rule the OverlaySelector UI applies when offering these designs.
        const designCategory: SceneCategory = validation.sceneCategory;

        const state = this.getState();
        const voiceDrivenDefaultSceneDesignIds = {
            defaultTalkingHeadSceneDesignId: getTemplateDefaultTalkingHeadSceneDesign(
                state,
                currentTemplateId,
            ),
            defaultVoiceoverSceneDesignId: getTemplateDefaultVoiceoverSceneDesign(
                state,
                currentTemplateId,
            ),
        };
        if (
            !isOverlaySceneDesign(designCategory, scene_design_id, voiceDrivenDefaultSceneDesignIds)
        ) {
            const isBaseDesign =
                scene_design_id ===
                    voiceDrivenDefaultSceneDesignIds.defaultTalkingHeadSceneDesignId ||
                scene_design_id === voiceDrivenDefaultSceneDesignIds.defaultVoiceoverSceneDesignId;
            return isBaseDesign
                ? `Error: Scene design "${scene_design_id}" is the base scene design of voice-driven scene groups and cannot be used for an added scene. Use ${TOOL_NAMES.listSceneDesigns} and choose a different design.`
                : `Error: Scene design "${scene_design_id}" (category "${designCategory}") cannot be used for a scene added to a voice-driven scene group. Use ${TOOL_NAMES.listSceneDesigns} and choose a design with a compatible category.`;
        }
        if (
            isVoiceoverSceneGroup(sceneGroup) &&
            !isAIAvatarSceneGroup(sceneGroup) &&
            designCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT
        ) {
            return `Error: Scene design "${scene_design_id}" is a talking-head insert design and cannot be used for a scene added to a voiceover scene group. Use ${TOOL_NAMES.listSceneDesigns} and choose a different design.`;
        }

        let startTimeMillis: number;
        let endTimeMillis: number;
        if (cover_entire_video) {
            // A full-video scene spans the whole scene group, so creating one absorbs
            // every other added scene into it — silently wiping work when a scene
            // group has already been built out. cover_entire_video is meant for a
            // fresh scene group getting a single full-video treatment (e.g. an
            // audiogram/waveform design), so refuse it once other scenes have been
            // added and point back to word-bounded coverage for filling gaps.
            const existingAddedSceneCount = sceneGroup.scenes.filter((scene) =>
                isSceneAnOverlay(state, scene),
            ).length;
            if (existingAddedSceneCount > 0) {
                return `Error: cover_entire_video would replace the ${existingAddedSceneCount} scene(s) already added to this scene group with a single full-length scene. To fill an uncovered range, add a word-bounded scene covering just the uncovered words instead. cover_entire_video is only for a scene group with no other added scenes yet that should get a full-video treatment (e.g. an audiogram/waveform design).`;
            }

            const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
            if (playbackRegions.length > 0) {
                startTimeMillis = playbackRegions[0][0];
                endTimeMillis = playbackRegions[playbackRegions.length - 1][1];
            } else {
                const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                if (!sourceDuration) {
                    return `Error: Could not determine the duration of scene group ${scene_group_index}, so a full-video scene can't be created.`;
                }
                startTimeMillis = 0;
                endTimeMillis = sourceDuration;
            }
        } else {
            const { resolveOverlayRange } = makeOperationsCallable(
                { resolveOverlayRangeOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            const range = resolveOverlayRange(sceneGroup, requestedWords!);
            if ('error' in range) {
                return `Error: ${range.error}`;
            }
            ({ startTimeMillis, endTimeMillis } = range);
        }

        try {
            const { moveOtherOverlaysAndCreateOverlay } = makeOperationsCallable(
                { moveOtherOverlaysAndCreateOverlayOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const sceneIdsBefore = this.snapshotSceneIds();

            const newSceneId = moveOtherOverlaysAndCreateOverlay(
                sceneGroup.id,
                startTimeMillis,
                endTimeMillis,
                scene_design_id,
                false, // systemTriggered
                parentActionGroup,
            );

            // Mark the new scene as "building" so the build preview blurs
            // it until the content it needs lands (set_text/set_media/image_ask).
            this.startSceneBuilding(newSceneId);
            // Creating the overlay also splits the base scene underneath it, so the
            // remainder of the scene group is a new base scene that needs blurring too.
            this.blurScenesCreatedSince(sceneIdsBefore, [newSceneId]);

            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );

            // Report the new scene's stable scene_id (the handle the edit tools address it by) and
            // the words it covers, so the agent sets text/media on the right scene by scene_id —
            // keeping content aligned to the narration it belongs to even when it batches all the
            // adds first and fills content afterwards.
            const shortSceneId =
                computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id)).get(
                    newSceneId,
                ) ?? newSceneId;
            const coveredDescription = cover_entire_video
                ? 'the entire video'
                : `"${requestedWords!.join(' ')}"`;
            return `Success: Added scene covering ${coveredDescription} (scene_id: ${shortSceneId}). Set this scene's text/media with ${TOOL_NAMES.setText}/${TOOL_NAMES.setMedia} addressing it by scene_id "${shortSceneId}". Match the content to the words it covers.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding scene: ${errorMessage}`;
        }
    }
}
